<template lang="pug" src="./v-map.pug"> </template>
<style lang="scss" src="./v-map.scss"> </style>

<script>
import { mapGetters, mapState } from 'vuex';

import { LMap, LTileLayer, LMarker, LPopup, LIcon, LPolyline } from 'vue2-leaflet';
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
import loader from '@/utils/loader';
import config from '../../../config.json';
import request from '@/utils/request';
import detectMobileDevice from '@/utils/mobileDeviceCheck';
import { validationMixin } from 'vuelidate';
import { required, maxLength } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';
import { copyToClipboard } from '@/utils/helpers';

export default {
    components: {
        'l-marker-cluster': Vue2LeafletMarkerCluster,
        LMap,
        LTileLayer,
        LMarker,
        LIcon,
        LPopup,
        LPolyline
    },
    mixins: [validationMixin, validate],
    validations: {
        comment: {
            required,
            maxLength: maxLength(250)
        }
    },
    props: {
        forOperators: {
            type: Boolean,
            default: true
        },
        isShowGroupOrders: {
            type: Boolean,
            default: false
        },
        isShowMenu: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        driverLoader: null,
        driversLoading: false,
        url: config.mapUrl,
        bounds: null,
        zoom: config.mapViewZoom,
        center: config.mapViewCoords,
        markerObjects: null,
        driversMap: [],
        statuses: {
            accepted: '/img/icons/pins/driver-enroute@2x.png',
            arrived: '/img/icons/pins/driver-busy@2x.png',
            landed: '/img/icons/pins/driver-busy@2x.png',
            false: '/img/icons/pins/driver-online@2x.png'
        },
        markerIcons: {
            accepted: '/img/icons/pins/marker-accepted.png',
            completed: '/img/icons/pins/marker-completed.png',
            cancelled: '/img/icons/pins/marker-completed.png'
        },
        orderStatusColors: {
            accepted: '#283594',
            arrived: '#fa5457',
            landed: '#fc0394',
            pause: '#03dffc'
        },
        options: {},
        requestProcessing: false,
        showAdditionalFilter: false,
        selectedOptionClass: null,
        driverLikeOrderId: null,
        comment: null
    }),
    computed: {
        ...mapGetters(['user']),
        ...mapState({
            polyline: (state) => state.orders.polyline,
            lines: (state) => state.orders.lines,
            coordinates: (state) => state.orders.coordinates,
            addressCoords: (state) => state.orders.addressCoords,
            groupOrdersCoords: (state) => state.orders.groupOrdersCoords,
            activeDriver: (state) => state.orders.activeDriver,
            optionClasses: (state) => state.data.optionClasses
        }),
        filteredDrivers() {
            if (!this.callSign && !this.driversLoading) {
                return [];
            }

            return this.driversMap;
        },
        showAddressCoords() {
            return this.addressCoords && this.addressCoords.latitude && this.addressCoords.longitude;
        },
        callSign: {
            get() {
                return this.$store.state.orders.driverCallSign;
            },
            set(val) {
                if (!val) {
                    this.selectedOptionClass = null;
                    this.showAdditionalFilter = false;
                }

                this.$store.commit('orders/SET_CALL_SIGN', val || null);
            }
        },
        driversResetIcon() {
            return !this.driversLoading  ? 'mdi-car-off' : 'mdi-car';
        },
        isMobileDevice() {
            return detectMobileDevice();
        },
        filtersStateIcon() {
            return this.showAdditionalFilter ? 'mdi-close' : 'mdi-dots-vertical';
        }
    },
    watch: {
        showAddressCoords(val) {
            if (val) {
                this.center = [this.addressCoords.latitude, this.addressCoords.longitude];
                this.zoom = config.mapViewZoom;
            }
        }
    },
    created() {
        if (this.callSign) {
            this.startLoadDrivers();
        }

        this.$store.dispatch('data/getOptionClasses');
    },
    beforeMount() {
        const mapOptions = {
            scrollWheelZoom: false,
            touchZoom: false,
            doubleClickZoom: false
        };

        if (this.isMobileDevice && !this.forOperators) {
            Object.assign(this.options, mapOptions);
        }
    },
    mounted() {
        if (this.activeDriver) {
            this.focusDriver(this.activeDriver);
        }
    },
    beforeDestroy() {
        if (this.driverLoader) {
            this.driverLoader.cancel();
        }

        this.$store.commit('orders/SET_ORDER_LINES', []);
        this.$store.commit('orders/SET_POLYLINE', []);
    },
    methods: {
        copy(phone) {
            copyToClipboard(phone.replace(/\+38/, ''));
            this.$store.dispatch('alert/addMessage', {
              type: 'success',
              message: 'success'
            }, { root: true });
        },

        getDriverIcon(driver) {
            return this.statuses[driver.busyStatus];
        },

        customMarkerIcon(status) {
            return ['accepted', 'completed', 'cancelled'].includes(status);
        },

        toggleAdditionalFilter() {
            this.showAdditionalFilter = !this.showAdditionalFilter;

            if (!this.showAdditionalFilter) {
                this.onOptionClassChange(null);
            }
        },

        onOptionClassChange(optionClass) {
            this.selectedOptionClass = optionClass;
            this.startLoadDrivers();
        },

        changeShowDrivers() {
            this.callSign = null;
            this.driversLoading = !this.driversLoading;

            if (!this.driversLoading) {
                this.onOptionClassChange(null);
                this.showAdditionalFilter = false;
                return this.cancelLoadDrivers();
            }

            this.startLoadDrivers();
        },

        startLoadDrivers(forDashboard = false) {
            this.removeLoader();

            if (!this.callSign && !this.driversLoading && !forDashboard) return;

            this.driversLoading = true;

            this.driverLoader = loader.drivers('/drivers/map', {
                callSign: this.callSign,
                optionClass: this.selectedOptionClass
            }, config.driversPollingInterval, (driversMap) => {
                this.driversMap = driversMap;

                if (
                    this.callSign &&
                    this.driversMap.length == 1 &&
                    this.driversMap[0].callSign.match(this.callSign)
                ) {
                    this.$nextTick(() => {
                        const driver = this.filteredDrivers[0];

                        this.focusDriver(driver);
                        this.$store.commit('orders/SET_ACTIVE_DRIVER', driver);
                    });
                }
            });
        },

        focusDriver(driver) {
            if (driver.latitude && driver.longitude) {
                this.center = [driver.latitude, driver.longitude];
            }

            this.zoom = config.mapViewZoom;

            const ref = this.$refs[`markersRef_${driver.id}`];

            if (ref && ref[0] && ref[0].mapObject) {
                setTimeout(() => {
                    ref[0].mapObject.openPopup();
                }, 200);
            }
        },

        cancelLoadDrivers() {
            this.driversMap = [];
            this.driversLoading = false;

            this.removeLoader();
        },

        removeLoader() {
            if (this.driverLoader) {
                this.driverLoader.cancel();
                this.driverLoader = null;
            }
        },

        zoomUpdated (zoom) {
            this.zoom = zoom;
        },

        centerUpdated (center) {
            this.center = center;
        },

        async showDriverPath(orderId) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({
                method: 'get',
                url:`/orders/${orderId}/driverPath`,
            }).then(res => {
                if (res.data) {
                    this.$store.commit('orders/SET_ORDER_LINES', res.data.lines);
                    this.$store.commit('orders/SET_POLYLINE', res.data.coords.map(it => {
                        return [it.latitude, it.longitude];
                    }));

                    const center = this.polyline && this.polyline[0] || this.lines &&
                        this.lines[0] && this.lines[0].polyline && this.lines[0].polyline[0];

                    if (center) {
                        this.center = center;
                        this.zoom = config.mapViewZoom;
                    }
                }
            }).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    const message = this.$vuetify.lang.t(`$vuetify.pages.DashboardMap.errors.${error.response.data.error}`);

                    this.$store.dispatch('alert/addMessage', {
                        message,
                        locale: false
                    });
                }
            }).finally(() => {
                setTimeout(() => {
                    this.requestProcessing = false;
                }, 500);
            });
        },

        toggleDrawer: function() {
            this.$store.commit('data/SET_DRAWER', !this.drawer);
        },

        statusColor: function(line) {
            return line.pause ? this.orderStatusColors['pause'] : this.orderStatusColors[line.status];
        },

        showLine(line) {
            return line && line.polyline && !['cancelled', 'completed'].includes(line.status)
                && line.polyline.length > 1;
        },

        async submitDriverLike(driverId, like) {
            if (this.$v.$invalid) {
                this.$v.$touch();
                return;
            }

            this.$store.dispatch('data/createDriverLike', {
                driverId,
                like,
                comment: this.comment,
                orderId: this.driverLikeOrderId
            }).then(() => {
                this.comment = null;
                this.driverLikeOrderId = null;
            });
        }
    }
};
</script>
